import React, { useEffect, useState } from 'react';

import Buttons from '@components/Buttons';
import PaymentMethods from '@components/Payment/PaymentMethods';
import ProgressIndicator from '@components/ProgressIndicator';
import * as Types from '@typings/graphql-models';

import useNoodleApi from '@hooks/useNoodleApi';
import {
  cancelNonStripeSubscrtiption,
  deleteUserSubscriptionStripe,
  getUserSubscriptions,
  getUserSubscriptionStripe,
  setDefaultPaymentMethodStripe,
} from '@tsClient';
import * as tsClient from '@tsClient';
import useToast, { ToastTypeVariants } from '@hooks/useToast';
import { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import { useUser } from '@providers/Auth';
import { SubscriptionStatus } from '@typings/graphql-models';
import Link from '@components/CustomLink';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import s from './SingleSubscription.module.scss';

type PaymentMethod = Pick<StripePaymentMethod, 'id'> & Parameters<typeof PaymentMethods>[0]['method'];

type Subscription = Awaited<ReturnType<typeof getUserSubscriptions>>[0];

type Props<S> = {
  creatorSlug: string;
  creatorId: string;
  stripeAccountId: string;
  subscription: S;
  setActiveSubscription: (s: S | null) => void;
  retryPayment: (id: string) => Promise<void>;
};

const SingleSubscription = <S extends Subscription>({
  creatorSlug,
  subscription,
  creatorId,
  stripeAccountId,
  setActiveSubscription,
  retryPayment,
}: Props<S>): React.ReactElement => {
  const { subscriptionStatus, canceledAt } = subscription;

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string>('');
  const [isSubscriptionCanceling, setIsSubscriptionCanceling] = useState<boolean>(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<number>(0);

  const { getData: getPaymentMethodsForPersonFn } = useNoodleApi(tsClient.paymentMethods.getPaymentMethodsForPerson);
  const { getData: detachCardFn } = useNoodleApi(tsClient.paymentMethods.deletePaymentMethod);
  const { getData: setDefaultPaymentMethodStripeFn } = useNoodleApi(setDefaultPaymentMethodStripe);
  const { getData: deleteUserSubscriptionStripeFn } = useNoodleApi(deleteUserSubscriptionStripe);
  const { getData: cancelNonStripeSubscrtiptionFn } = useNoodleApi(cancelNonStripeSubscrtiption);
  const { getData: getUserSubscriptionStripeFn, fetchingState } = useNoodleApi(getUserSubscriptionStripe);

  const addToast = useToast();
  const [user] = useUser();

  const stripeSubscriptionId = subscription?.stripeOrderId || null;

  useEffect(() => {
    const getDefaultPaymentMethods = async (): Promise<void> => {
      if (stripeAccountId && stripeSubscriptionId) {
        const { data: sub } = await getUserSubscriptionStripeFn({
          stripeAccountId,
          subscriptionId: stripeSubscriptionId,
        });

        if (!sub?.subscription?.default_payment_method) {
          if (paymentMethods?.[0]?.id) {
            setDefaultPaymentMethodId(paymentMethods[0].id);
          }
          return;
        }

        if (sub?.subscription?.cancel_at_period_end) {
          setCancelAtPeriodEnd(sub.subscription.current_period_end * 1000);
        }
        if (sub?.subscription?.default_payment_method) {
          if (typeof sub.subscription.default_payment_method === 'string') {
            setDefaultPaymentMethodId(sub.subscription.default_payment_method);
          } else {
            setDefaultPaymentMethodId(sub.subscription.default_payment_method.id);
          }
        }
      }
    };
    getDefaultPaymentMethods();
  }, [paymentMethods, stripeAccountId, stripeSubscriptionId, getUserSubscriptionStripeFn]);

  useEffect(() => {
    const getPaymentMethods = async (): Promise<void> => {
      if (user) {
        const { data: payments } = await getPaymentMethodsForPersonFn({
          creatorId,
          personId: user.id,
        });
        setPaymentMethods(payments?.items || []);
      }
    };
    if (creatorId && !user?.isAnonymous) {
      getPaymentMethods();
    }
  }, [creatorId, getPaymentMethodsForPersonFn, user]);

  const handleDeleteSubscription = async (): Promise<void> => {
    setIsSubscriptionCanceling(true);

    if (stripeAccountId && subscription.stripeOrderId) {
      await deleteUserSubscriptionStripeFn({
        stripeAccountId,
        subscriptionId: subscription.stripeOrderId,
      });
    } else {
      await cancelNonStripeSubscrtiptionFn({ subscriptionId: subscription.id });
    }

    addToast(
      ToastTypeVariants.SUCCESS,
      'You have canceled your subscription and it will not renew. You will have access until the next billing period',
    );

    setActiveSubscription(null);
  };

  const handleChangeDefaultPaymentMethod = async (paymentMethodId: string): Promise<void> => {
    if (paymentMethodId && stripeAccountId && subscription.stripeOrderId) {
      await setDefaultPaymentMethodStripeFn({
        paymentMethodId,
        stripeAccountId,
        subscriptionId: subscription.stripeOrderId,
      });
      setDefaultPaymentMethodId(paymentMethodId);
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId: string): Promise<void> => {
    await detachCardFn({
      creatorId,
      paymentMethodId,
    });
    setPaymentMethods(paymentMethods.filter(({ id }) => id !== paymentMethodId));
  };

  return (
    <>
      <div className={s['subscription-item__wrapper']}>
        {isSubscriptionCanceling && <ProgressIndicator isAbsolute />}
        {fetchingState.isFetching && <ProgressIndicator />}
        {subscriptionStatus === SubscriptionStatus.PastDue && (
          <p className='caption'>
            {defaultPaymentMethodId
              ? 'You need to retry your charge to continue access. You can retry with your current payment method or add a new payment method and retry.'
              : 'You need to update your payment method to continue access.'
            }
          </p>
        )}
        {!!paymentMethods.length && fetchingState.isFetched && (
          <PaymentMethods
            subscription={subscription}
            creatorSlug={creatorSlug}
            method={paymentMethods.find((m) => m.id === defaultPaymentMethodId) || null}
            handleRemovePaymentMethod={handleRemovePaymentMethod}
            isSubscriptionActive={subscriptionStatus === Types.SubscriptionStatus.Active}
            handleChangeDefaultPaymentMethod={handleChangeDefaultPaymentMethod}
            retryPayment={retryPayment}
          />
        )}
        {subscriptionStatus === Types.SubscriptionStatus.PastDue && (
          <Link
            to={getUrl(IDENTIFIERS.USER_PROFILE_ADD_PAYMENT_METHOD, { creatorSlug, returnTo: getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug }) })}
          >
            <Buttons isSecondary isFullWidth>
              Add new payment method
            </Buttons>
          </Link>
        )}
        {!canceledAt && subscriptionStatus !== Types.SubscriptionStatus.Canceled && (
          <Buttons onClick={handleDeleteSubscription} isThird disabled={Boolean(cancelAtPeriodEnd)}>
            <span
              style={{
                color: subscriptionStatus === Types.SubscriptionStatus.Active ? 'var(--color-error)' : 'var(--color-gray-75)',
              }}
            >
              Cancel Subscription
            </span>
          </Buttons>
        )}
      </div>
    </>
  );
};

export default SingleSubscription;
